import "./CompanyAccounting.css";

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import axios from "axios";
import { toast } from "react-toastify";

const API_URL = "/api";

const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem(
    "company_token"
  )}`,
});

const formatDateInput = (value) => {
  const date = value instanceof Date
    ? value
    : new Date(value);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(
    2,
    "0"
  );
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const formatDate = (value) => {
  if (!value) return "-";

  const dateValue = String(value).slice(0, 10);

  return new Date(
    `${dateValue}T00:00:00`
  ).toLocaleDateString("es-CO");
};

const formatDateTime = (value) => {
  if (!value) return "-";

  return new Date(value).toLocaleString("es-CO");
};

const money = (value) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const getErrorMessage = (error) => {
  const detail = error?.response?.data?.detail;

  if (typeof detail === "string") {
    return detail;
  }

  if (Array.isArray(detail)) {
    return detail
      .map((item) => item.msg)
      .join(". ");
  }

  return "No fue posible completar la operación.";
};

const emptyLine = () => ({
  account_id: "",
  cost_center_id: "",
  description: "",
  debit: "",
  credit: "",
});

const getEntryTotals = (entry) =>
  (entry.lines || []).reduce(
    (totals, line) => ({
      debit:
        totals.debit + Number(line.debit || 0),
      credit:
        totals.credit + Number(line.credit || 0),
    }),
    {
      debit: 0,
      credit: 0,
    }
  );

const getMonthName = (month) => {
  const months = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ];

  return months[Number(month) - 1] || "";
};

function CompanyAccounting() {
  const currentDate = new Date();

  const [accounts, setAccounts] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [entries, setEntries] = useState([]);
  const [entriesTotal, setEntriesTotal] = useState(0);
  const [trialBalance, setTrialBalance] = useState(null);
  const [entryDate, setEntryDate] = useState(formatDateInput(currentDate));
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([
    emptyLine(),
    emptyLine(),
  ]);

  const [dateFrom, setDateFrom] = useState(
    formatDateInput(
      new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        1
      )
    )
  );
  const [dateTo, setDateTo] = useState(
    formatDateInput(currentDate)
  );
  const [entryStatus, setEntryStatus] =
    useState("");

  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);

  const [activeBook, setActiveBook] =
    useState("entries");

  const [journalBook, setJournalBook] = useState({
    items: [],
    total: 0,
    page: 1,
    page_size: 30,
  });
  const [journalBookPage, setJournalBookPage] =
    useState(1);

  const [ledgerAccountId, setLedgerAccountId] =
    useState("");
  const [generalLedger, setGeneralLedger] =
    useState(null);
  const [ledgerPage, setLedgerPage] = useState(1);

  const [incomeStatement, setIncomeStatement] =
    useState(null);
  const [balanceSheet, setBalanceSheet] =
    useState(null);

  const [cashFlow, setCashFlow] = useState(null);
  const [
    costCenterIncomeStatement,
    setCostCenterIncomeStatement,
  ] = useState(null);

  const [periods, setPeriods] = useState([]);
  const [periodYear, setPeriodYear] = useState(
    String(currentDate.getFullYear())
  );
  const [periodMonth, setPeriodMonth] = useState(
    String(currentDate.getMonth() + 1)
  );
  const [periodNotes, setPeriodNotes] = useState("");

  const [yearCloses, setYearCloses] = useState([]);

  const [yearCloseYear, setYearCloseYear] = useState(
    String(currentDate.getFullYear())
  );

  const [
    yearCloseResultAccountId,
    setYearCloseResultAccountId,
  ] = useState("");

  const [yearCloseNotes, setYearCloseNotes] =
    useState("");

  const [closingYear, setClosingYear] =
    useState(false);

  const [loading, setLoading] = useState(true);
  const [loadingBook, setLoadingBook] =
    useState(false);
  const [loadingFinancial, setLoadingFinancial] =
    useState(false);
  const [savingEntry, setSavingEntry] =
    useState(false);
  const [closingPeriod, setClosingPeriod] =
    useState(false);
  const [reopeningPeriodId, setReopeningPeriodId] =
    useState(null);

  const [auditLogs, setAuditLogs] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(1);

  const [auditAction, setAuditAction] = useState("");
  const [auditEntityType, setAuditEntityType] = useState("");
  const [selectedAuditLog, setSelectedAuditLog] = useState(null);

  const [auditUsers, setAuditUsers] = useState([]);
  const [auditUserId, setAuditUserId] = useState("");
  const [auditSearch, setAuditSearch] = useState("");

  const [loadingAudit, setLoadingAudit] = useState(false);

  const loadAccounts = useCallback(async () => {
    const response = await axios.get(
      `${API_URL}/accounting/accounts`,
      {
        headers: getHeaders(),
        params: {
          only_active: true,
        },
      }
    );

    const selectableAccounts = (
      Array.isArray(response.data)
        ? response.data
        : []
    ).filter(
      (account) =>
        account.is_active &&
        account.allows_posting
    );

    setAccounts(selectableAccounts);
  }, []);

  const loadCostCenters = useCallback(async () => {
    const response = await axios.get(
      `${API_URL}/cost-centers/`,
      {
        headers: getHeaders(),
        params: {
          active_only: true,
        },
      }
    );

    setCostCenters(
      Array.isArray(response.data)
        ? response.data
        : []
    );
  }, []);

  const loadEntries = useCallback(async () => {
    const params = {
      page,
      page_size: pageSize,
    };

    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (entryStatus) {
      params.entry_status = entryStatus;
    }

    const response = await axios.get(
      `${API_URL}/accounting/entries`,
      {
        headers: getHeaders(),
        params,
      }
    );

    setEntries(response.data.items || []);
    setEntriesTotal(response.data.total || 0);
  }, [
    dateFrom,
    dateTo,
    entryStatus,
    page,
    pageSize,
  ]);

  const loadTrialBalance = useCallback(async () => {
    const params = {};

    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;

    const response = await axios.get(
      `${API_URL}/accounting/trial-balance`,
      {
        headers: getHeaders(),
        params,
      }
    );

    setTrialBalance(response.data);
  }, [dateFrom, dateTo]);

  const loadPeriods = useCallback(async () => {
    const response = await axios.get(
      `${API_URL}/accounting/periods/`,
      {
        headers: getHeaders(),
      }
    );

    setPeriods(
      Array.isArray(response.data)
        ? response.data
        : []
    );
  }, []);

  const loadYearCloses = useCallback(async () => {
    const response = await axios.get(
      `${API_URL}/accounting/year-closes/`,
      {
        headers: getHeaders(),
      }
    );

    setYearCloses(
      Array.isArray(response.data)
        ? response.data
        : []
    );
  }, []);

  const loadJournalBook = useCallback(async () => {
    setLoadingBook(true);

    try {
      const response = await axios.get(
        `${API_URL}/accounting/journal-book`,
        {
          headers: getHeaders(),
          params: {
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined,
            page: journalBookPage,
            page_size: 30,
          },
        }
      );

      setJournalBook(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoadingBook(false);
    }
  }, [
    dateFrom,
    dateTo,
    journalBookPage,
  ]);

  const loadGeneralLedger = useCallback(async () => {
    if (!ledgerAccountId) {
      setGeneralLedger(null);
      return;
    }

    setLoadingBook(true);

    try {
      const response = await axios.get(
        `${API_URL}/accounting/general-ledger/${ledgerAccountId}`,
        {
          headers: getHeaders(),
          params: {
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined,
            page: ledgerPage,
            page_size: 50,
          },
        }
      );

      setGeneralLedger(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoadingBook(false);
    }
  }, [
    ledgerAccountId,
    dateFrom,
    dateTo,
    ledgerPage,
  ]);

  const loadIncomeStatement = useCallback(async () => {
    setLoadingFinancial(true);

    try {
      const response = await axios.get(
        `${API_URL}/accounting/income-statement`,
        {
          headers: getHeaders(),
          params: {
            date_from: dateFrom,
            date_to: dateTo,
          },
        }
      );

      setIncomeStatement(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoadingFinancial(false);
    }
  }, [dateFrom, dateTo]);

  const loadBalanceSheet = useCallback(async () => {
    setLoadingFinancial(true);

    try {
      const response = await axios.get(
        `${API_URL}/accounting/balance-sheet`,
        {
          headers: getHeaders(),
          params: {
            as_of_date: dateTo,
          },
        }
      );

      setBalanceSheet(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoadingFinancial(false);
    }
  }, [dateTo]);

  const loadCashFlow = useCallback(async () => {
    setLoadingFinancial(true);

    try {
      const response = await axios.get(
        `${API_URL}/accounting/cash-flow`,
        {
          headers: getHeaders(),
          params: {
            date_from: dateFrom,
            date_to: dateTo,
          },
        }
      );

      setCashFlow(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoadingFinancial(false);
    }
  }, [dateFrom, dateTo]);
  const loadCostCenterIncomeStatement =
    useCallback(async () => {
      setLoadingFinancial(true);

      try {
        const response = await axios.get(
          `${API_URL}/accounting/cost-center-income-statement`,
          {
            headers: getHeaders(),
            params: {
              date_from: dateFrom,
              date_to: dateTo,
            },
          }
        );

        setCostCenterIncomeStatement(response.data);
      } catch (error) {
        toast.error(getErrorMessage(error));
      } finally {
        setLoadingFinancial(false);
      }
    }, [dateFrom, dateTo]);

  const loadAuditLogs = useCallback(async () => {
    setLoadingAudit(true);

    try {
      const response = await axios.get(
        `${API_URL}/accounting/audit-logs`,
        {
          headers: getHeaders(),
          params: {
            page: auditPage,
            page_size: 30,
            action: auditAction || undefined,
            entity_type: auditEntityType || undefined,
            user_id: auditUserId
              ? Number(auditUserId)
              : undefined,
            search: auditSearch.trim() || undefined,
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined,
          },
        }
      );

      setAuditLogs(response.data.items || []);
      setAuditTotal(response.data.total || 0);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoadingAudit(false);
    }
  }, [
    auditPage,
    auditAction,
    auditEntityType,
    auditUserId,
    auditSearch,
    dateFrom,
    dateTo,
  ]);

  const loadAuditUsers = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/accounting/audit-users`,
        {
          headers: getHeaders(),
        }
      );

      setAuditUsers(response.data || []);
    } catch (error) {
      toast.error(getErrorMessage(error));
      setAuditUsers([]);
    }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);

    try {
      await Promise.all([
        loadAccounts(),
        loadCostCenters(),
        loadEntries(),
        loadTrialBalance(),
        loadPeriods(),
        loadYearCloses(),
      ]);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [
    loadAccounts,
    loadCostCenters,
    loadEntries,
    loadTrialBalance,
    loadPeriods,
    loadYearCloses,
  ]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (activeBook === "journal") {
      loadJournalBook();
    }
  }, [activeBook, loadJournalBook]);

  useEffect(() => {
    if (activeBook === "ledger") {
      loadGeneralLedger();
    }
  }, [activeBook, loadGeneralLedger]);

  useEffect(() => {
    if (activeBook === "income") {
      loadIncomeStatement();
    }
  }, [activeBook, loadIncomeStatement]);

  useEffect(() => {
    if (activeBook === "balance-sheet") {
      loadBalanceSheet();
    }
  }, [activeBook, loadBalanceSheet]);

  useEffect(() => {
    if (activeBook === "cash-flow") {
      loadCashFlow();
    }
  }, [activeBook, loadCashFlow]);

  useEffect(() => {
    if (activeBook === "cost-centers") {
      loadCostCenterIncomeStatement();
    }
  }, [
    activeBook,
    loadCostCenterIncomeStatement,
  ]);

  useEffect(() => {
    if (activeBook === "audit") {
      loadAuditLogs();
      loadAuditUsers();
    }
  }, [
    activeBook,
    loadAuditLogs,
    loadAuditUsers,
  ]);

  const totals = useMemo(
    () =>
      lines.reduce(
        (result, line) => ({
          debit:
            result.debit +
            Number(line.debit || 0),
          credit:
            result.credit +
            Number(line.credit || 0),
        }),
        {
          debit: 0,
          credit: 0,
        }
      ),
    [lines]
  );

  const isBalanced =
    totals.debit > 0 &&
    Math.abs(totals.debit - totals.credit) <=
    0.01;

  const totalPages = Math.max(
    1,
    Math.ceil(entriesTotal / pageSize)
  );

  const journalBookTotalPages = Math.max(
    1,
    Math.ceil(
      Number(journalBook.total || 0) /
      Number(journalBook.page_size || 30)
    )
  );

  const ledgerTotalPages = Math.max(
    1,
    Math.ceil(
      Number(generalLedger?.total || 0) /
      Number(generalLedger?.page_size || 50)
    )
  );

  const selectedPeriod = useMemo(
    () =>
      periods.find(
        (period) =>
          Number(period.year) === Number(periodYear) &&
          Number(period.month) === Number(periodMonth)
      ),
    [
      periods,
      periodYear,
      periodMonth,
    ]
  );

  const closedPeriods = useMemo(
    () =>
      periods
        .filter((period) => period.status === "closed")
        .sort((first, second) => {
          if (first.year !== second.year) {
            return second.year - first.year;
          }

          return second.month - first.month;
        }),
    [periods]
  );

  const isSelectedPeriodClosed =
    selectedPeriod?.status === "closed";

  const addLine = () => {
    setLines((current) => [
      ...current,
      emptyLine(),
    ]);
  };

  const removeLine = (index) => {
    if (lines.length <= 2) {
      toast.error(
        "Un asiento debe tener mínimo dos líneas."
      );
      return;
    }

    setLines((current) =>
      current.filter(
        (_, currentIndex) =>
          currentIndex !== index
      )
    );
  };

  const updateLine = (index, field, value) => {
    setLines((current) =>
      current.map((line, currentIndex) => {
        if (currentIndex !== index) {
          return line;
        }

        const updatedLine = {
          ...line,
          [field]: value,
        };

        if (
          field === "debit" &&
          Number(value) > 0
        ) {
          updatedLine.credit = "";
        }

        if (
          field === "credit" &&
          Number(value) > 0
        ) {
          updatedLine.debit = "";
        }

        return updatedLine;
      })
    );
  };

  const resetEntryForm = () => {
    setEntryDate(formatDateInput(new Date()));
    setDescription("");
    setNotes("");
    setLines([emptyLine(), emptyLine()]);
  };

  const handleEntrySubmit = async (event) => {
    event.preventDefault();

    if (!description.trim()) {
      toast.error(
        "Ingresa la descripción del asiento."
      );
      return;
    }

    if (!isBalanced) {
      toast.error(
        "El asiento debe tener débitos y créditos iguales."
      );
      return;
    }

    const invalidLine = lines.find(
      (line) =>
        !line.account_id ||
        (
          Number(line.debit || 0) === 0 &&
          Number(line.credit || 0) === 0
        )
    );

    if (invalidLine) {
      toast.error(
        "Cada línea debe tener una cuenta y un valor."
      );
      return;
    }

    setSavingEntry(true);

    try {
      await axios.post(
        `${API_URL}/accounting/entries`,
        {
          entry_date: entryDate,
          description: description.trim(),
          notes: notes.trim() || null,
          lines: lines.map((line) => ({
            account_id: Number(line.account_id),
            cost_center_id: line.cost_center_id ? Number(line.cost_center_id) : null,
            description:
              line.description.trim() || null,
            debit: Number(line.debit || 0),
            credit: Number(line.credit || 0),
          })),
        },
        {
          headers: getHeaders(),
        }
      );

      toast.success(
        "Asiento contable registrado correctamente."
      );

      resetEntryForm();
      setPage(1);
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSavingEntry(false);
    }
  };

  const handleReverseEntry = async (entry) => {
    const reason = window.prompt(
      `Motivo de reversión del asiento ${entry.number}:`
    );

    if (reason === null) return;

    if (reason.trim().length < 3) {
      toast.error(
        "Indica un motivo de al menos 3 caracteres."
      );
      return;
    }

    try {
      await axios.post(
        `${API_URL}/accounting/entries/${entry.id}/reverse`,
        {
          reason: reason.trim(),
          reversal_date: formatDateInput(new Date()),
        },
        {
          headers: getHeaders(),
        }
      );

      toast.success(
        "Se creó el comprobante de reversión correctamente."
      );

      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleDateFilter = async (event) => {
    event.preventDefault();

    if (dateFrom && dateTo && dateFrom > dateTo) {
      toast.error(
        "La fecha inicial no puede ser mayor que la fecha final."
      );
      return;
    }

    setPage(1);
    setJournalBookPage(1);
    setLedgerPage(1);

    await loadData();

    if (activeBook === "journal") {
      await loadJournalBook();
    }

    if (activeBook === "ledger") {
      await loadGeneralLedger();
    }

    if (activeBook === "income") {
      await loadIncomeStatement();
    }

    if (activeBook === "balance-sheet") {
      await loadBalanceSheet();
    }

    if (activeBook === "cash-flow") {
      await loadCashFlow();
    }

    if (activeBook === "cost-centers") {
      await loadCostCenterIncomeStatement();
    }
  };

  const handleClosePeriod = async (event) => {
    event.preventDefault();

    const year = Number(periodYear);
    const month = Number(periodMonth);

    if (
      !Number.isInteger(year) ||
      !Number.isInteger(month) ||
      month < 1 ||
      month > 12
    ) {
      toast.error(
        "Selecciona un año y un mes válidos."
      );
      return;
    }

    if (isSelectedPeriodClosed) {
      toast.error(
        "Este período ya se encuentra cerrado."
      );
      return;
    }

    const confirmed = window.confirm(
      `¿Confirmas el cierre de ${getMonthName(
        month
      )} de ${year}? Después no se podrán contabilizar movimientos en este período.`
    );

    if (!confirmed) return;

    setClosingPeriod(true);

    try {
      await axios.post(
        `${API_URL}/accounting/periods/${year}/${month}/close`,
        {
          notes: periodNotes.trim() || null,
        },
        {
          headers: getHeaders(),
        }
      );

      toast.success("Período cerrado correctamente.");
      setPeriodNotes("");

      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setClosingPeriod(false);
    }
  };

  const handleReopenPeriod = async (period) => {
    const note = window.prompt(
      `Motivo para reabrir ${getMonthName(
        period.month
      )} de ${period.year}:`
    );

    if (note === null) return;

    if (note.trim().length < 3) {
      toast.error(
        "Indica un motivo de al menos 3 caracteres."
      );
      return;
    }

    const confirmed = window.confirm(
      "¿Confirmas la reapertura? Se permitirán nuevamente movimientos en este período."
    );

    if (!confirmed) return;

    setReopeningPeriodId(period.id);

    try {
      await axios.post(
        `${API_URL}/accounting/periods/${period.year}/${period.month}/reopen`,
        {
          note: note.trim(),
        },
        {
          headers: getHeaders(),
        }
      );

      toast.success(
        "Período reabierto correctamente."
      );

      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setReopeningPeriodId(null);
    }
  };

  const handleRefresh = async () => {
    await loadData();

    if (activeBook === "journal") {
      await loadJournalBook();
    }

    if (activeBook === "ledger") {
      await loadGeneralLedger();
    }

    if (activeBook === "income") {
      await loadIncomeStatement();
    }

    if (activeBook === "balance-sheet") {
      await loadBalanceSheet();
    }

    if (activeBook === "cash-flow") {
      await loadCashFlow();
    }

    if (activeBook === "cost-centers") {
      await loadCostCenterIncomeStatement();
    }
  };

  const getStatusLabel = (value) => {
    const labels = {
      draft: "Borrador",
      posted: "Contabilizado",
      void: "Anulado",
    };

    return labels[value] || value;
  };

  const getTypeLabel = (value) => {
    const labels = {
      ACTIVO: "Activo",
      PASIVO: "Pasivo",
      PATRIMONIO: "Patrimonio",
      INGRESO: "Ingreso",
      GASTO: "Gasto",
      COSTO: "Costo",
    };

    return labels[value] || value;
  };

  const equityAccounts = useMemo(
    () =>
      accounts.filter(
        (account) =>
          account.account_type === "PATRIMONIO" &&
          account.is_active &&
          account.allows_posting
      ),
    [accounts]
  );

  const closedYears = useMemo(
    () =>
      new Set(
        yearCloses.map((yearClose) =>
          Number(yearClose.year)
        )
      ),
    [yearCloses]
  );

  const handleYearClose = async (event) => {
    event.preventDefault();

    const year = Number(yearCloseYear);
    const resultAccountId = Number(
      yearCloseResultAccountId
    );

    if (
      !Number.isInteger(year) ||
      year < 2000 ||
      year > currentDate.getFullYear()
    ) {
      toast.error("Selecciona un año válido.");
      return;
    }

    if (!resultAccountId) {
      toast.error(
        "Selecciona la cuenta de resultado del ejercicio."
      );
      return;
    }

    if (closedYears.has(year)) {
      toast.error(
        "Este año ya tiene un cierre contable registrado."
      );
      return;
    }

    const confirmed = window.confirm(
      `¿Confirmas el cierre contable anual ${year}? ` +
      "Esta operación creará un asiento automático para cancelar ingresos, costos y gastos."
    );

    if (!confirmed) {
      return;
    }

    setClosingYear(true);

    try {
      await axios.post(
        `${API_URL}/accounting/year-closes/`,
        {
          year,
          result_account_id: resultAccountId,
          notes: yearCloseNotes.trim() || null,
        },
        {
          headers: getHeaders(),
        }
      );

      toast.success(
        `Cierre contable anual ${year} generado correctamente.`
      );

      setYearCloseNotes("");
      setYearCloseResultAccountId("");

      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setClosingYear(false);
    }
  };
  const handleExportAudit = async (format) => {
    try {
      const response = await axios.get(
        `${API_URL}/accounting/audit-logs/export/${format}`,
        {
          headers: getHeaders(),
          params: {
            entity_type: auditEntityType || undefined,
            action: auditAction || undefined,
            user_id: auditUserId
              ? Number(auditUserId)
              : undefined,
            search: auditSearch.trim() || undefined,
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined,
          },
          responseType: "blob",
        }
      );

      const extension = format === "excel" ? "xlsx" : "pdf";

      const blobUrl = URL.createObjectURL(
        new Blob([response.data])
      );

      const link = document.createElement("a");

      link.href = blobUrl;
      link.download = `auditoria_contable.${extension}`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      URL.revokeObjectURL(blobUrl);

      toast.success(
        `Auditoría exportada correctamente en ${extension.toUpperCase()}.`
      );
    } catch (error) {
      toast.error(
        "No fue posible exportar la auditoría contable."
      );
    }
  };

  return (
    <div className="company-accounting">
      <section className="accounting-heading">
        <div>
          <p className="accounting-kicker">
            CONTABILIDAD
          </p>

          <h1>Asientos y balances</h1>

          <p>
            Registra comprobantes, consulta libros y
            genera estados financieros.
          </p>
        </div>

        <div className="accounting-header-actions">
          <button
            type="button"
            className="btn-accounting-refresh"
            onClick={handleRefresh}
            disabled={loading}
          >
            {loading
              ? "Actualizando..."
              : "Actualizar"}
          </button>
        </div>
      </section>

      {accounts.length === 0 && !loading && (
        <section className="accounting-alert">
          <strong>
            No hay cuentas auxiliares disponibles.
          </strong>

          <span>
            Crea o activa cuentas que permitan
            movimientos desde el Plan Único de Cuentas.
          </span>
        </section>
      )}

      <section className="accounting-card accounting-period-card">
        <div className="accounting-card-heading">
          <div>
            <h2>Cierre de período contable</h2>

            <p>
              Bloquea movimientos, anulaciones y
              reversos del período cerrado.
            </p>
          </div>

          <span
            className={
              isSelectedPeriodClosed
                ? "accounting-period-status closed"
                : "accounting-period-status open"
            }
          >
            {isSelectedPeriodClosed
              ? "Período cerrado"
              : "Período abierto"}
          </span>
        </div>

        <form
          className="accounting-period-form"
          onSubmit={handleClosePeriod}
        >
          <label>
            Año

            <input
              type="number"
              min="2000"
              max={currentDate.getFullYear()}
              value={periodYear}
              onChange={(event) =>
                setPeriodYear(event.target.value)
              }
              required
            />
          </label>

          <label>
            Mes

            <select
              value={periodMonth}
              onChange={(event) =>
                setPeriodMonth(event.target.value)
              }
            >
              {Array.from(
                { length: 12 },
                (_, index) => index + 1
              ).map((month) => (
                <option key={month} value={month}>
                  {getMonthName(month)}
                </option>
              ))}
            </select>
          </label>

          <label className="accounting-period-notes">
            Observación

            <input
              type="text"
              value={periodNotes}
              maxLength="500"
              onChange={(event) =>
                setPeriodNotes(event.target.value)
              }
              placeholder="Observación del cierre"
            />
          </label>

          <button
            type="submit"
            className="btn-accounting-close-period"
            disabled={
              closingPeriod ||
              isSelectedPeriodClosed
            }
          >
            {closingPeriod
              ? "Cerrando..."
              : isSelectedPeriodClosed
                ? "Período cerrado"
                : "Cerrar período"}
          </button>
        </form>
      </section>

      <section className="accounting-card accounting-period-history-card">
        <div className="accounting-card-heading">
          <div>
            <h2>Períodos cerrados</h2>

            <p>
              Historial de cierres y reaperturas
              autorizadas.
            </p>
          </div>

          <span className="accounting-period-counter">
            {closedPeriods.length} cerrado(s)
          </span>
        </div>

        {closedPeriods.length === 0 ? (
          <p className="accounting-empty-periods">
            No existen períodos cerrados todavía.
          </p>
        ) : (
          <div className="accounting-periods-table-wrapper">
            <table className="accounting-periods-table">
              <thead>
                <tr>
                  <th>Período</th>
                  <th>Estado</th>
                  <th>Fecha de cierre</th>
                  <th>Observación</th>
                  <th>Acción</th>
                </tr>
              </thead>

              <tbody>
                {closedPeriods.map((period) => (
                  <tr key={period.id}>
                    <td>
                      {getMonthName(period.month)}{" "}
                      {period.year}
                    </td>

                    <td>
                      <span className="accounting-period-status closed">
                        Cerrado
                      </span>
                    </td>

                    <td>
                      {formatDateTime(period.closed_at)}
                    </td>

                    <td>
                      {period.notes ||
                        "Sin observación de cierre."}
                    </td>

                    <td>
                      <button
                        type="button"
                        className="btn-accounting-reopen"
                        disabled={
                          reopeningPeriodId === period.id
                        }
                        onClick={() =>
                          handleReopenPeriod(period)
                        }
                      >
                        {reopeningPeriodId === period.id
                          ? "Reabriendo..."
                          : "Reabrir"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="accounting-card accounting-year-close-card">
        <div className="accounting-card-heading">
          <div>
            <h2>Cierre contable anual</h2>

            <p>
              Cancela ingresos, costos y gastos contra la
              cuenta de resultado del ejercicio.
            </p>
          </div>

          <span className="accounting-period-counter">
            {yearCloses.length} cierre(s)
          </span>
        </div>

        <form
          className="accounting-period-form"
          onSubmit={handleYearClose}
        >
          <label>
            Año a cerrar

            <input
              type="number"
              min="2000"
              max={currentDate.getFullYear()}
              value={yearCloseYear}
              onChange={(event) =>
                setYearCloseYear(event.target.value)
              }
              required
            />
          </label>

          <label>
            Cuenta de resultado del ejercicio

            <select
              value={yearCloseResultAccountId}
              onChange={(event) =>
                setYearCloseResultAccountId(
                  event.target.value
                )
              }
              required
            >
              <option value="">
                Selecciona una cuenta de patrimonio
              </option>

              {equityAccounts.map((account) => (
                <option
                  key={account.id}
                  value={account.id}
                >
                  {account.code} — {account.name}
                </option>
              ))}
            </select>
          </label>

          <label className="accounting-period-notes">
            Observación del cierre

            <input
              type="text"
              maxLength="500"
              value={yearCloseNotes}
              onChange={(event) =>
                setYearCloseNotes(event.target.value)
              }
              placeholder="Ejemplo: Cierre anual aprobado por gerencia"
            />
          </label>

          <button
            type="submit"
            className="btn-accounting-close-period"
            disabled={
              closingYear ||
              closedYears.has(Number(yearCloseYear)) ||
              equityAccounts.length === 0
            }
          >
            {closingYear
              ? "Generando cierre..."
              : closedYears.has(Number(yearCloseYear))
                ? "Año cerrado"
                : "Cerrar año"}
          </button>
        </form>

        {equityAccounts.length === 0 && (
          <p className="accounting-empty-periods">
            No hay cuentas auxiliares activas de tipo
            Patrimonio. Crea una cuenta como “Resultado del
            ejercicio” antes de cerrar el año.
          </p>
        )}

        {yearCloses.length > 0 && (
          <div className="accounting-periods-table-wrapper">
            <table className="accounting-periods-table">
              <thead>
                <tr>
                  <th>Año</th>
                  <th>Cuenta resultado</th>
                  <th>Comprobante</th>
                  <th>Fecha de cierre</th>
                  <th>Observación</th>
                </tr>
              </thead>

              <tbody>
                {yearCloses.map((yearClose) => {
                  const account = accounts.find(
                    (item) =>
                      Number(item.id) ===
                      Number(yearClose.result_account_id)
                  );

                  return (
                    <tr key={yearClose.id}>
                      <td>{yearClose.year}</td>

                      <td>
                        {account
                          ? `${account.code} — ${account.name}`
                          : `Cuenta #${yearClose.result_account_id}`}
                      </td>

                      <td>
                        {yearClose.closing_entry_id
                          ? `Asiento #${yearClose.closing_entry_id}`
                          : "-"}
                      </td>

                      <td>
                        {formatDateTime(
                          yearClose.closed_at
                        )}
                      </td>

                      <td>
                        {yearClose.notes ||
                          "Sin observación."}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="accounting-card accounting-entry-card">
        <div className="accounting-card-heading">
          <div>
            <h2>Nuevo asiento contable</h2>

            <p>
              Todo asiento debe tener el mismo total
              en débitos y créditos.
            </p>
          </div>

          <span
            className={
              isBalanced
                ? "accounting-balance-status balanced"
                : "accounting-balance-status unbalanced"
            }
          >
            {isBalanced
              ? "Asiento balanceado"
              : "Pendiente de balancear"}
          </span>
        </div>

        <form onSubmit={handleEntrySubmit}>
          <div className="accounting-entry-data">
            <label>
              Fecha

              <input
                type="date"
                value={entryDate}
                onChange={(event) =>
                  setEntryDate(event.target.value)
                }
                required
              />
            </label>

            <label className="accounting-description-field">
              Descripción

              <input
                type="text"
                value={description}
                onChange={(event) =>
                  setDescription(event.target.value)
                }
                placeholder="Ejemplo: Ajuste de caja"
                required
              />
            </label>
          </div>

          <label className="accounting-notes-field">
            Observaciones

            <textarea
              rows="2"
              value={notes}
              onChange={(event) =>
                setNotes(event.target.value)
              }
              placeholder="Información adicional"
            />
          </label>

          <div className="accounting-table-wrapper">
            <table className="accounting-entry-table">
              <thead>
                <tr>
                  <th>Cuenta contable</th>
                  <th>Centro de costo</th>
                  <th>Descripción línea</th>
                  <th>Débito</th>
                  <th>Crédito</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {lines.map((line, index) => (
                  <tr key={index}>
                    <td>
                      <select
                        value={line.account_id}
                        onChange={(event) =>
                          updateLine(
                            index,
                            "account_id",
                            event.target.value
                          )
                        }
                        required
                      >
                        <option value="">
                          Selecciona una cuenta
                        </option>

                        {accounts.map((account) => (
                          <option
                            key={account.id}
                            value={account.id}
                          >
                            {account.code} — {account.name}
                          </option>
                        ))}
                      </select>
                    </td>

                    <td>
                      <select
                        value={line.cost_center_id}
                        onChange={(event) =>
                          updateLine(
                            index,
                            "cost_center_id",
                            event.target.value
                          )
                        }
                      >
                        <option value="">
                          Sin centro de costo
                        </option>

                        {costCenters.map((costCenter) => (
                          <option
                            key={costCenter.id}
                            value={costCenter.id}
                          >
                            {costCenter.code} — {costCenter.name}
                          </option>
                        ))}
                      </select>
                    </td>

                    <td>
                      <input
                        type="text"
                        value={line.description}
                        onChange={(event) =>
                          updateLine(
                            index,
                            "description",
                            event.target.value
                          )
                        }
                        placeholder="Detalle opcional"
                      />
                    </td>

                    <td>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={line.debit}
                        onChange={(event) =>
                          updateLine(
                            index,
                            "debit",
                            event.target.value
                          )
                        }
                        placeholder="0"
                      />
                    </td>

                    <td>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={line.credit}
                        onChange={(event) =>
                          updateLine(
                            index,
                            "credit",
                            event.target.value
                          )
                        }
                        placeholder="0"
                      />
                    </td>

                    <td>
                      <button
                        type="button"
                        className="btn-accounting-remove-line"
                        onClick={() => removeLine(index)}
                      >
                        Quitar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>

              <tfoot>
                <tr>
                  <td colSpan="3">
                    <button
                      type="button"
                      className="btn-accounting-add-line"
                      onClick={addLine}
                    >
                      + Agregar línea
                    </button>
                  </td>

                  <td>
                    <strong>
                      {money(totals.debit)}
                    </strong>
                  </td>

                  <td>
                    <strong>
                      {money(totals.credit)}
                    </strong>
                  </td>

                  <td />
                </tr>
              </tfoot>
            </table>
          </div>

          <button
            type="submit"
            className="btn-accounting-save"
            disabled={
              savingEntry ||
              accounts.length === 0 ||
              !isBalanced
            }
          >
            {savingEntry
              ? "Registrando asiento..."
              : "Contabilizar asiento"}
          </button>
        </form>
      </section>

      <section className="accounting-card accounting-filters-card">
        <form
          className="accounting-filters"
          onSubmit={handleDateFilter}
        >
          <label>
            Desde

            <input
              type="date"
              value={dateFrom}
              onChange={(event) =>
                setDateFrom(event.target.value)
              }
            />
          </label>

          <label>
            Hasta

            <input
              type="date"
              value={dateTo}
              onChange={(event) =>
                setDateTo(event.target.value)
              }
            />
          </label>

          <label>
            Estado

            <select
              value={entryStatus}
              onChange={(event) => {
                setEntryStatus(event.target.value);
                setPage(1);
              }}
            >
              <option value="">Todos</option>
              <option value="posted">
                Contabilizados
              </option>
              <option value="void">
                Anulados
              </option>
              <option value="draft">
                Borradores
              </option>
            </select>
          </label>

          <button type="submit">
            Aplicar filtro
          </button>
        </form>
      </section>

      <section className="accounting-book-tabs">
        <button
          type="button"
          className={
            activeBook === "entries" ? "active" : ""
          }
          onClick={() => setActiveBook("entries")}
        >
          Comprobantes
        </button>

        <button
          type="button"
          className={
            activeBook === "journal" ? "active" : ""
          }
          onClick={() => setActiveBook("journal")}
        >
          Libro diario
        </button>

        <button
          type="button"
          className={
            activeBook === "ledger" ? "active" : ""
          }
          onClick={() => setActiveBook("ledger")}
        >
          Libro mayor
        </button>

        <button
          type="button"
          className={
            activeBook === "income" ? "active" : ""
          }
          onClick={() => setActiveBook("income")}
        >
          Estado de resultados
        </button>

        <button
          type="button"
          className={
            activeBook === "balance-sheet"
              ? "active"
              : ""
          }
          onClick={() =>
            setActiveBook("balance-sheet")
          }
        >
          Balance general
        </button>

        <button
          type="button"
          className={
            activeBook === "cash-flow"
              ? "active"
              : ""
          }
          onClick={() => setActiveBook("cash-flow")}
        >
          Flujo de efectivo
        </button>
        <button
          type="button"
          className={
            activeBook === "cost-centers"
              ? "active"
              : ""
          }
          onClick={() => setActiveBook("cost-centers")}
        >
          Resultados por centro
        </button>
        <button
          type="button"
          className={activeBook === "audit" ? "active" : ""}
          onClick={() => setActiveBook("audit")}
        >
          Auditoría
        </button>
      </section>

      {activeBook === "entries" && (
        <>
          <section className="accounting-balance-grid">
            <article className="accounting-card trial-balance-card">
              <div className="accounting-card-heading">
                <div>
                  <h2>Balance de comprobación</h2>

                  <p>
                    Movimientos contabilizados en el
                    período seleccionado.
                  </p>
                </div>

                <span
                  className={
                    trialBalance?.is_balanced
                      ? "accounting-balance-status balanced"
                      : "accounting-balance-status unbalanced"
                  }
                >
                  {trialBalance?.is_balanced
                    ? "Balance cuadrado"
                    : "Revisar balance"}
                </span>
              </div>

              <div className="accounting-balance-totals">
                <span>
                  Débitos:
                  <strong>
                    {money(trialBalance?.total_debit)}
                  </strong>
                </span>

                <span>
                  Créditos:
                  <strong>
                    {money(trialBalance?.total_credit)}
                  </strong>
                </span>
              </div>

              <div className="accounting-table-wrapper">
                <table className="accounting-balance-table">
                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Cuenta</th>
                      <th>Centro de costo</th>
                      <th>Tipo</th>
                      <th>Débitos</th>
                      <th>Créditos</th>
                      <th>Saldo</th>
                    </tr>
                  </thead>

                  <tbody>
                    {(trialBalance?.accounts || []).length ===
                      0 ? (
                      <tr>
                        <td
                          colSpan="7"
                          className="accounting-empty"
                        >
                          No hay movimientos contables en
                          este período.
                        </td>
                      </tr>
                    ) : (
                      trialBalance.accounts.map((account) => (
                        <tr key={account.account_id}>
                          <td>{account.account_code}</td>

                          <td>
                            <strong>
                              {account.account_name}
                            </strong>
                          </td>

                          <td>
                            <span className="accounting-account-type">
                              {getTypeLabel(
                                account.account_type
                              )}
                            </span>
                          </td>

                          <td className="accounting-debit">
                            {money(account.total_debit)}
                          </td>

                          <td className="accounting-credit">
                            {money(account.total_credit)}
                          </td>

                          <td className="accounting-money">
                            {money(
                              account.closing_balance
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </article>
          </section>

          <section className="accounting-card accounting-journal-card">
            <div className="accounting-card-heading">
              <div>
                <h2>Comprobantes contables</h2>

                <p>
                  Asientos registrados en el período
                  seleccionado.
                </p>
              </div>
            </div>

            <div className="accounting-table-wrapper">
              <table className="accounting-journal-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Número</th>
                    <th>Descripción</th>
                    <th>Débitos</th>
                    <th>Créditos</th>
                    <th>Estado</th>
                    <th>Acción</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan="7"
                        className="accounting-empty"
                      >
                        Cargando comprobantes...
                      </td>
                    </tr>
                  ) : entries.length === 0 ? (
                    <tr>
                      <td
                        colSpan="7"
                        className="accounting-empty"
                      >
                        No hay comprobantes para mostrar.
                      </td>
                    </tr>
                  ) : (
                    entries.map((entry) => {
                      const entryTotals =
                        getEntryTotals(entry);

                      return (
                        <tr key={entry.id}>
                          <td>
                            {formatDate(entry.entry_date)}
                          </td>

                          <td>
                            <strong>{entry.number}</strong>
                          </td>

                          <td>
                            {entry.description}

                            {entry.notes && (
                              <small>{entry.notes}</small>
                            )}
                          </td>

                          <td className="accounting-debit">
                            {money(entryTotals.debit)}
                          </td>

                          <td className="accounting-credit">
                            {money(entryTotals.credit)}
                          </td>

                          <td>
                            <span
                              className={`accounting-entry-status ${entry.status}`}
                            >
                              {getStatusLabel(
                                entry.status
                              )}
                            </span>
                          </td>

                          <td>
                            {entry.status === "posted" ? (
                              <button
                                type="button"
                                className="btn-accounting-void"
                                onClick={() =>
                                  handleReverseEntry(entry)
                                }
                              >
                                Reversar
                              </button>
                            ) : (
                              <span className="accounting-no-action">
                                —
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="accounting-pagination">
              <span>
                Mostrando {entries.length} de{" "}
                {entriesTotal} comprobante(s)
              </span>

              <div>
                <button
                  type="button"
                  onClick={() =>
                    setPage((current) =>
                      Math.max(1, current - 1)
                    )
                  }
                  disabled={page === 1}
                >
                  Anterior
                </button>

                <span>
                  Página {page} de {totalPages}
                </span>

                <button
                  type="button"
                  onClick={() =>
                    setPage((current) =>
                      Math.min(
                        totalPages,
                        current + 1
                      )
                    )
                  }
                  disabled={page >= totalPages}
                >
                  Siguiente
                </button>
              </div>
            </div>
          </section>
        </>
      )}

      {activeBook === "journal" && (
        <section className="accounting-card accounting-journal-card">
          <div className="accounting-card-heading">
            <div>
              <h2>Libro diario</h2>

              <p>
                Asientos contabilizados ordenados
                cronológicamente.
              </p>
            </div>
          </div>

          <div className="accounting-table-wrapper">
            <table className="accounting-journal-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Comprobante</th>
                  <th>Descripción</th>
                  <th>Cuenta</th>
                  <th>Débito</th>
                  <th>Crédito</th>
                </tr>
              </thead>

              <tbody>
                {loadingBook ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="accounting-empty"
                    >
                      Cargando Libro Diario...
                    </td>
                  </tr>
                ) : (journalBook.items || []).length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="accounting-empty"
                    >
                      No hay movimientos para mostrar.
                    </td>
                  </tr>
                ) : (
                  journalBook.items.map((entry) =>
                    (entry.lines || []).map(
                      (line, index) => (
                        <tr
                          key={`${entry.id}-${line.account_id}-${index}`}
                        >
                          <td>
                            {index === 0
                              ? formatDate(entry.entry_date)
                              : ""}
                          </td>

                          <td>
                            {index === 0
                              ? entry.number
                              : ""}
                          </td>

                          <td>
                            {index === 0
                              ? entry.description
                              : line.description || ""}
                          </td>

                          <td>
                            {line.account_code} —{" "}
                            {line.account_name}
                          </td>
                          <td>
                            {line.cost_center_code
                              ? `${line.cost_center_code} — ${line.cost_center_name}`
                              : "Sin centro de costo"}
                          </td>

                          <td className="accounting-debit">
                            {money(line.debit)}
                          </td>

                          <td className="accounting-credit">
                            {money(line.credit)}
                          </td>
                        </tr>
                      )
                    )
                  )
                )}
              </tbody>
            </table>
          </div>

          <div className="accounting-pagination">
            <span>
              Mostrando{" "}
              {journalBook.items?.length || 0} de{" "}
              {journalBook.total || 0} comprobante(s)
            </span>

            <div>
              <button
                type="button"
                onClick={() =>
                  setJournalBookPage((current) =>
                    Math.max(1, current - 1)
                  )
                }
                disabled={journalBookPage === 1}
              >
                Anterior
              </button>

              <span>
                Página {journalBookPage} de{" "}
                {journalBookTotalPages}
              </span>

              <button
                type="button"
                onClick={() =>
                  setJournalBookPage((current) =>
                    Math.min(
                      journalBookTotalPages,
                      current + 1
                    )
                  )
                }
                disabled={
                  journalBookPage >= journalBookTotalPages
                }
              >
                Siguiente
              </button>
            </div>
          </div>
        </section>
      )}

      {activeBook === "ledger" && (
        <section className="accounting-card accounting-journal-card">
          <div className="accounting-card-heading">
            <div>
              <h2>Libro mayor</h2>

              <p>
                Consulta los movimientos y saldo
                acumulado de una cuenta PUC.
              </p>
            </div>
          </div>

          <div className="accounting-filters">
            <label>
              Cuenta PUC

              <select
                value={ledgerAccountId}
                onChange={(event) => {
                  setLedgerAccountId(event.target.value);
                  setLedgerPage(1);
                }}
              >
                <option value="">
                  Selecciona una cuenta contable
                </option>

                {accounts.map((account) => (
                  <option
                    key={account.id}
                    value={account.id}
                  >
                    {account.code} — {account.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {!ledgerAccountId ? (
            <p className="accounting-empty">
              Selecciona una cuenta PUC para consultar
              el Libro Mayor.
            </p>
          ) : loadingBook ? (
            <p className="accounting-empty">
              Cargando Libro Mayor...
            </p>
          ) : !generalLedger ? (
            <p className="accounting-empty">
              No fue posible cargar el Libro Mayor.
            </p>
          ) : (
            <>
              <div className="accounting-balance-totals">
                <span>
                  Cuenta:
                  <strong>
                    {generalLedger.account_code} —{" "}
                    {generalLedger.account_name}
                  </strong>
                </span>

                <span>
                  Saldo inicial:
                  <strong>
                    {money(
                      generalLedger.opening_balance
                    )}
                  </strong>
                </span>

                <span>
                  Saldo final:
                  <strong>
                    {money(
                      generalLedger.closing_balance
                    )}
                  </strong>
                </span>
              </div>

              <div className="accounting-table-wrapper">
                <table className="accounting-journal-table">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Comprobante</th>
                      <th>Descripción</th>
                      <th>Centro de costo</th>
                      <th>Débito</th>
                      <th>Crédito</th>
                      <th>Saldo acumulado</th>
                    </tr>
                  </thead>

                  <tbody>
                    {(generalLedger.items || []).length ===
                      0 ? (
                      <tr>
                        <td
                          colSpan="7"
                          className="accounting-empty"
                        >
                          No hay movimientos para esta
                          cuenta en el período seleccionado.
                        </td>
                      </tr>
                    ) : (
                      generalLedger.items.map((line, index) => (
                        <tr
                          key={`${line.entry_id}-${index}`}
                        >
                          <td>
                            {formatDate(line.entry_date)}
                          </td>

                          <td>
                            <strong>
                              {line.entry_number}
                            </strong>
                          </td>

                          <td>
                            {line.description}

                            {line.line_description && (
                              <small>
                                {line.line_description}
                              </small>
                            )}
                          </td>
                          <td>
                            {line.cost_center_code
                              ? `${line.cost_center_code} — ${line.cost_center_name}`
                              : "Sin centro de costo"}
                          </td>
                          <td className="accounting-debit">
                            {money(line.debit)}
                          </td>

                          <td className="accounting-credit">
                            {money(line.credit)}
                          </td>

                          <td className="accounting-money">
                            {money(line.balance)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="accounting-pagination">
                <span>
                  Mostrando{" "}
                  {generalLedger.items?.length || 0} de{" "}
                  {generalLedger.total || 0} movimiento(s)
                </span>

                <div>
                  <button
                    type="button"
                    onClick={() =>
                      setLedgerPage((current) =>
                        Math.max(1, current - 1)
                      )
                    }
                    disabled={ledgerPage === 1}
                  >
                    Anterior
                  </button>

                  <span>
                    Página {ledgerPage} de{" "}
                    {ledgerTotalPages}
                  </span>

                  <button
                    type="button"
                    onClick={() =>
                      setLedgerPage((current) =>
                        Math.min(
                          ledgerTotalPages,
                          current + 1
                        )
                      )
                    }
                    disabled={
                      ledgerPage >= ledgerTotalPages
                    }
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      )}

      {activeBook === "income" && (
        <section className="accounting-card">
          <div className="accounting-card-heading">
            <div>
              <h2>Estado de resultados</h2>

              <p>
                Resultado entre {formatDate(dateFrom)}
                {" y "}
                {formatDate(dateTo)}.
              </p>
            </div>
          </div>

          {loadingFinancial ? (
            <p className="accounting-empty">
              Generando Estado de Resultados...
            </p>
          ) : !incomeStatement ? (
            <p className="accounting-empty">
              No hay información disponible.
            </p>
          ) : (
            <>
              <div className="accounting-financial-grid">
                <article>
                  <h3>Ingresos</h3>

                  {(incomeStatement.income || []).map(
                    (item) => (
                      <div
                        className="accounting-financial-line"
                        key={`income-${item.account_id}`}
                      >
                        <span>
                          {item.account_code} —{" "}
                          {item.account_name}
                        </span>

                        <strong>{money(item.amount)}</strong>
                      </div>
                    )
                  )}

                  <div className="accounting-financial-total">
                    <span>Total ingresos</span>

                    <strong>
                      {money(
                        incomeStatement.total_income
                      )}
                    </strong>
                  </div>
                </article>

                <article>
                  <h3>Costos y gastos</h3>

                  {(incomeStatement.costs || []).map(
                    (item) => (
                      <div
                        className="accounting-financial-line"
                        key={`cost-${item.account_id}`}
                      >
                        <span>
                          {item.account_code} —{" "}
                          {item.account_name}
                        </span>

                        <strong>{money(item.amount)}</strong>
                      </div>
                    )
                  )}

                  {(incomeStatement.expenses || []).map(
                    (item) => (
                      <div
                        className="accounting-financial-line"
                        key={`expense-${item.account_id}`}
                      >
                        <span>
                          {item.account_code} —{" "}
                          {item.account_name}
                        </span>

                        <strong>{money(item.amount)}</strong>
                      </div>
                    )
                  )}

                  <div className="accounting-financial-total">
                    <span>Total costos</span>

                    <strong>
                      {money(
                        incomeStatement.total_costs
                      )}
                    </strong>
                  </div>

                  <div className="accounting-financial-total">
                    <span>Total gastos</span>

                    <strong>
                      {money(
                        incomeStatement.total_expenses
                      )}
                    </strong>
                  </div>
                </article>
              </div>

              <div className="accounting-financial-result">
                <span>Utilidad neta del período</span>

                <strong>
                  {money(incomeStatement.net_profit)}
                </strong>
              </div>
            </>
          )}
        </section>
      )}

      {activeBook === "balance-sheet" && (
        <section className="accounting-card">
          <div className="accounting-card-heading">
            <div>
              <h2>Balance general</h2>

              <p>
                Situación financiera al{" "}
                {formatDate(dateTo)}.
              </p>
            </div>

            {balanceSheet && (
              <span
                className={
                  balanceSheet.is_balanced
                    ? "accounting-balance-status balanced"
                    : "accounting-balance-status unbalanced"
                }
              >
                {balanceSheet.is_balanced
                  ? "Balance cuadrado"
                  : "Revisar balance"}
              </span>
            )}
          </div>

          {loadingFinancial ? (
            <p className="accounting-empty">
              Generando Balance General...
            </p>
          ) : !balanceSheet ? (
            <p className="accounting-empty">
              No hay información disponible.
            </p>
          ) : (
            <>
              <div className="accounting-financial-grid">
                <article>
                  <h3>Activos</h3>

                  {(balanceSheet.assets || []).map(
                    (item) => (
                      <div
                        className="accounting-financial-line"
                        key={`asset-${item.account_id}`}
                      >
                        <span>
                          {item.account_code} —{" "}
                          {item.account_name}
                        </span>

                        <strong>{money(item.amount)}</strong>
                      </div>
                    )
                  )}

                  <div className="accounting-financial-total">
                    <span>Total activos</span>

                    <strong>
                      {money(balanceSheet.total_assets)}
                    </strong>
                  </div>
                </article>

                <article>
                  <h3>Pasivos y patrimonio</h3>

                  {(balanceSheet.liabilities || []).map(
                    (item) => (
                      <div
                        className="accounting-financial-line"
                        key={`liability-${item.account_id}`}
                      >
                        <span>
                          {item.account_code} —{" "}
                          {item.account_name}
                        </span>

                        <strong>{money(item.amount)}</strong>
                      </div>
                    )
                  )}

                  {(balanceSheet.equity || []).map(
                    (item, index) => (
                      <div
                        className="accounting-financial-line"
                        key={`equity-${item.account_id || index}`}
                      >
                        <span>
                          {item.account_code
                            ? `${item.account_code} — `
                            : ""}
                          {item.account_name}
                        </span>

                        <strong>{money(item.amount)}</strong>
                      </div>
                    )
                  )}

                  <div className="accounting-financial-total">
                    <span>Total pasivos</span>

                    <strong>
                      {money(
                        balanceSheet.total_liabilities
                      )}
                    </strong>
                  </div>

                  <div className="accounting-financial-total">
                    <span>Total patrimonio</span>

                    <strong>
                      {money(balanceSheet.total_equity)}
                    </strong>
                  </div>
                </article>
              </div>

              <div className="accounting-financial-result">
                <span>Resultado acumulado</span>

                <strong>
                  {money(
                    balanceSheet.accumulated_result
                  )}
                </strong>
              </div>
            </>
          )}
        </section>
      )}
      {activeBook === "cash-flow" && (
        <section className="accounting-card">
          <div className="accounting-card-heading">
            <div>
              <h2>Flujo de efectivo</h2>

              <p>
                Movimientos de Caja y Bancos entre{" "}
                {formatDate(dateFrom)} y{" "}
                {formatDate(dateTo)}.
              </p>
            </div>
          </div>

          {loadingFinancial ? (
            <p className="accounting-empty">
              Calculando flujo de efectivo...
            </p>
          ) : !cashFlow ? (
            <p className="accounting-empty">
              No hay información disponible.
            </p>
          ) : (
            <>
              <div className="accounting-balance-totals">
                <span>
                  Saldo inicial:
                  <strong>
                    {money(
                      cashFlow.opening_cash_balance
                    )}
                  </strong>
                </span>

                <span>
                  Flujo neto:
                  <strong>
                    {money(cashFlow.net_cash_flow)}
                  </strong>
                </span>

                <span>
                  Saldo final:
                  <strong>
                    {money(
                      cashFlow.closing_cash_balance
                    )}
                  </strong>
                </span>
              </div>

              <div className="accounting-financial-grid">
                <article>
                  <h3>Actividades operativas</h3>

                  {(cashFlow.operating_inflows || []).map(
                    (item, index) => (
                      <div
                        className="accounting-financial-line"
                        key={`operating-in-${item.account_id}-${index}`}
                      >
                        <span>
                          Ingreso · {item.account_name}
                        </span>

                        <strong className="accounting-debit">
                          {money(item.net_amount)}
                        </strong>
                      </div>
                    )
                  )}

                  {(cashFlow.operating_outflows || []).map(
                    (item, index) => (
                      <div
                        className="accounting-financial-line"
                        key={`operating-out-${item.account_id}-${index}`}
                      >
                        <span>
                          Egreso · {item.account_name}
                        </span>

                        <strong className="accounting-credit">
                          {money(item.net_amount)}
                        </strong>
                      </div>
                    )
                  )}

                  <div className="accounting-financial-total">
                    <span>Flujo operativo neto</span>

                    <strong>
                      {money(
                        cashFlow.net_operating_cash_flow
                      )}
                    </strong>
                  </div>
                </article>

                <article>
                  <h3>Inversión y financiación</h3>

                  {(cashFlow.investing_inflows || []).map(
                    (item, index) => (
                      <div
                        className="accounting-financial-line"
                        key={`investing-in-${item.account_id}-${index}`}
                      >
                        <span>
                          Inversión ingreso ·{" "}
                          {item.account_name}
                        </span>

                        <strong className="accounting-debit">
                          {money(item.net_amount)}
                        </strong>
                      </div>
                    )
                  )}

                  {(cashFlow.investing_outflows || []).map(
                    (item, index) => (
                      <div
                        className="accounting-financial-line"
                        key={`investing-out-${item.account_id}-${index}`}
                      >
                        <span>
                          Inversión egreso ·{" "}
                          {item.account_name}
                        </span>

                        <strong className="accounting-credit">
                          {money(item.net_amount)}
                        </strong>
                      </div>
                    )
                  )}

                  {(cashFlow.financing_inflows || []).map(
                    (item, index) => (
                      <div
                        className="accounting-financial-line"
                        key={`financing-in-${item.account_id}-${index}`}
                      >
                        <span>
                          Financiación ingreso ·{" "}
                          {item.account_name}
                        </span>

                        <strong className="accounting-debit">
                          {money(item.net_amount)}
                        </strong>
                      </div>
                    )
                  )}

                  {(cashFlow.financing_outflows || []).map(
                    (item, index) => (
                      <div
                        className="accounting-financial-line"
                        key={`financing-out-${item.account_id}-${index}`}
                      >
                        <span>
                          Financiación egreso ·{" "}
                          {item.account_name}
                        </span>

                        <strong className="accounting-credit">
                          {money(item.net_amount)}
                        </strong>
                      </div>
                    )
                  )}

                  <div className="accounting-financial-total">
                    <span>Flujo de inversión neto</span>

                    <strong>
                      {money(
                        cashFlow.net_investing_cash_flow
                      )}
                    </strong>
                  </div>

                  <div className="accounting-financial-total">
                    <span>Flujo de financiación neto</span>

                    <strong>
                      {money(
                        cashFlow.net_financing_cash_flow
                      )}
                    </strong>
                  </div>
                </article>
              </div>

              <div className="accounting-financial-result">
                <span>Saldo final de efectivo</span>

                <strong>
                  {money(
                    cashFlow.closing_cash_balance
                  )}
                </strong>
              </div>
            </>
          )}
        </section>
      )}
      {activeBook === "cost-centers" && (
        <section className="accounting-card">
          <div className="accounting-card-heading">
            <div>
              <h2>
                Estado de resultados por centro de costo
              </h2>

              <p>
                Rentabilidad entre{" "}
                {formatDate(dateFrom)} y{" "}
                {formatDate(dateTo)}.
              </p>
            </div>
          </div>

          {loadingFinancial ? (
            <p className="accounting-empty">
              Generando resultados por centro de costo...
            </p>
          ) : !costCenterIncomeStatement ? (
            <p className="accounting-empty">
              No hay información disponible.
            </p>
          ) : (
            <>
              {(
                costCenterIncomeStatement.items || []
              ).length === 0 ? (
                <p className="accounting-empty">
                  No existen movimientos de ingresos,
                  costos o gastos para el período seleccionado.
                </p>
              ) : (
                <div className="accounting-cost-center-grid">
                  {costCenterIncomeStatement.items.map(
                    (center) => (
                      <article
                        className="accounting-cost-center-card"
                        key={
                          center.cost_center_id ||
                          "unassigned"
                        }
                      >
                        <header>
                          <span>
                            {center.cost_center_code ||
                              "SIN CÓDIGO"}
                          </span>

                          <h3>
                            {center.cost_center_name}
                          </h3>
                        </header>

                        <div className="accounting-financial-line">
                          <span>Ingresos</span>

                          <strong className="accounting-debit">
                            {money(center.total_income)}
                          </strong>
                        </div>

                        <div className="accounting-financial-line">
                          <span>Costos</span>

                          <strong className="accounting-credit">
                            {money(center.total_costs)}
                          </strong>
                        </div>

                        <div className="accounting-financial-line">
                          <span>Gastos</span>

                          <strong className="accounting-credit">
                            {money(center.total_expenses)}
                          </strong>
                        </div>

                        <div className="accounting-financial-total">
                          <span>Utilidad bruta</span>

                          <strong>
                            {money(center.gross_profit)}
                          </strong>
                        </div>

                        <div
                          className={
                            Number(center.net_profit) >= 0
                              ? "accounting-financial-result positive"
                              : "accounting-financial-result negative"
                          }
                        >
                          <span>Utilidad neta</span>

                          <strong>
                            {money(center.net_profit)}
                          </strong>
                        </div>

                        <details>
                          <summary>
                            Ver detalle contable
                          </summary>

                          {(center.income || []).map(
                            (item) => (
                              <div
                                className="accounting-financial-line"
                                key={`income-${center.cost_center_id}-${item.account_id}`}
                              >
                                <span>
                                  Ingreso · {item.account_code} —{" "}
                                  {item.account_name}
                                </span>

                                <strong>
                                  {money(item.amount)}
                                </strong>
                              </div>
                            )
                          )}

                          {(center.costs || []).map(
                            (item) => (
                              <div
                                className="accounting-financial-line"
                                key={`cost-${center.cost_center_id}-${item.account_id}`}
                              >
                                <span>
                                  Costo · {item.account_code} —{" "}
                                  {item.account_name}
                                </span>

                                <strong>
                                  {money(item.amount)}
                                </strong>
                              </div>
                            )
                          )}

                          {(center.expenses || []).map(
                            (item) => (
                              <div
                                className="accounting-financial-line"
                                key={`expense-${center.cost_center_id}-${item.account_id}`}
                              >
                                <span>
                                  Gasto · {item.account_code} —{" "}
                                  {item.account_name}
                                </span>

                                <strong>
                                  {money(item.amount)}
                                </strong>
                              </div>
                            )
                          )}
                        </details>
                      </article>
                    )
                  )}
                </div>
              )}
            </>
          )}
        </section>
      )}
      {activeBook === "audit" && (
        <section className="accounting-card accounting-audit-card">
          <div className="accounting-card-heading">
            <div>
              <h2>Auditoría y trazabilidad</h2>
              <p>
                Consulta quién creó, editó, anuló o reversó
                cada comprobante contable.
              </p>
            </div>

            <div className="accounting-audit-heading-actions">
              <button
                type="button"
                className="btn-excel"
                onClick={() => handleExportAudit("excel")}
              >
                Exportar Excel
              </button>

              <button
                type="button"
                className="btn-pdf"
                onClick={() => handleExportAudit("pdf")}
              >
                Exportar PDF
              </button>

              <span className="accounting-period-counter">
                {auditTotal} evento(s)
              </span>
            </div>
          </div>

          <div className="accounting-audit-filters">
            <label>
              Buscar
              <input
                type="search"
                value={auditSearch}
                onChange={(event) => {
                  setAuditSearch(event.target.value);
                  setAuditPage(1);
                }}
                placeholder="Comprobante, venta, compra o descripción"
              />
            </label>

            <label>
              Usuario
              <select
                value={auditUserId}
                onChange={(event) => {
                  setAuditUserId(event.target.value);
                  setAuditPage(1);
                }}
              >
                <option value="">Todos los usuarios</option>

                {auditUsers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                    {user.email ? ` · ${user.email}` : ""}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Tipo de documento
              <select
                value={auditEntityType}
                onChange={(event) => {
                  setAuditEntityType(event.target.value);
                  setAuditPage(1);
                }}
              >
                <option value="">Todos</option>
                <option value="journal_entry">
                  Comprobantes contables
                </option>
                <option value="sale">Ventas</option>
                <option value="purchase">Compras</option>
                <option value="cash_movement">
                  Movimientos de caja
                </option>
                <option value="bank_movement">
                  Movimientos bancarios
                </option>
                <option value="bank_transfer">
                  Transferencias bancarias
                </option>
              </select>
            </label>

            <label>
              Acción
              <select
                value={auditAction}
                onChange={(event) => {
                  setAuditAction(event.target.value);
                  setAuditPage(1);
                }}
              >
                <option value="">Todas</option>
                <option value="create">Creación</option>
                <option value="update">Actualización</option>
                <option value="post">Contabilización</option>
                <option value="void">Anulación</option>
                <option value="reverse">Reversión</option>
              </select>
            </label>

            <div className="accounting-audit-filter-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setAuditSearch("");
                  setAuditUserId("");
                  setAuditEntityType("");
                  setAuditAction("");
                  setAuditPage(1);
                }}
              >
                Limpiar
              </button>

              <button
                type="button"
                className="btn-primary"
                onClick={() => {
                  setAuditPage(1);
                  loadAuditLogs();
                }}
              >
                Filtrar
              </button>
            </div>
          </div>

          {loadingAudit ? (
            <p className="accounting-empty">
              Cargando trazabilidad...
            </p>
          ) : auditLogs.length === 0 ? (
            <p className="accounting-empty">
              No existen eventos de auditoría para los filtros
              seleccionados.
            </p>
          ) : (
            <>
              <div className="accounting-table-wrapper">
                <table className="accounting-table">
                  <thead>
                    <tr>
                      <th>Fecha y hora</th>
                      <th>Usuario</th>
                      <th>Documento</th>
                      <th>Acción</th>
                      <th>Descripción</th>
                      <th />
                    </tr>
                  </thead>

                  <tbody>
                    {auditLogs.map((log) => (
                      <tr key={log.id}>
                        <td>
                          {new Date(
                            log.created_at
                          ).toLocaleString("es-CO")}
                        </td>

                        <td>
                          {log.user_name || "Sistema"}
                        </td>

                        <td>
                          {log.journal_entry_number
                            ? `Comprobante ${log.journal_entry_number}`
                            : `${log.entity_type} #${log.entity_id || "-"}`}
                        </td>

                        <td>
                          <span
                            className={`audit-action audit-action-${log.action}`}
                          >
                            {log.action === "create" && "Creación"}
                            {log.action === "update" && "Edición"}
                            {log.action === "post" && "Contabilización"}
                            {log.action === "void" && "Anulación"}
                            {log.action === "reverse" && "Reversión"}
                          </span>
                        </td>

                        <td>{log.description}</td>

                        <td>
                          <button
                            type="button"
                            className="btn-link"
                            onClick={() => setSelectedAuditLog(log)}
                          >
                            Ver detalle
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="accounting-pagination">
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={auditPage === 1}
                  onClick={() =>
                    setAuditPage((current) =>
                      Math.max(1, current - 1)
                    )
                  }
                >
                  Anterior
                </button>

                <span>
                  Página {auditPage} de{" "}
                  {Math.max(1, Math.ceil(auditTotal / 30))}
                </span>

                <button
                  type="button"
                  className="btn-secondary"
                  disabled={auditPage * 30 >= auditTotal}
                  onClick={() =>
                    setAuditPage((current) => current + 1)
                  }
                >
                  Siguiente
                </button>
              </div>
            </>
          )}

          {selectedAuditLog && (
            <div
              className="accounting-audit-modal-backdrop"
              onClick={() => setSelectedAuditLog(null)}
            >
              <article
                className="accounting-audit-modal"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="accounting-card-heading">
                  <div>
                    <h2>Detalle de auditoría</h2>
                    <p>{selectedAuditLog.description}</p>
                  </div>

                  <button
                    type="button"
                    className="btn-link"
                    onClick={() => setSelectedAuditLog(null)}
                  >
                    Cerrar
                  </button>
                </div>

                <h3>Valores anteriores</h3>

                <pre className="accounting-audit-json">
                  {JSON.stringify(
                    selectedAuditLog.previous_values || {},
                    null,
                    2
                  )}
                </pre>

                <h3>Valores nuevos</h3>

                <pre className="accounting-audit-json">
                  {JSON.stringify(
                    selectedAuditLog.new_values || {},
                    null,
                    2
                  )}
                </pre>
              </article>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

export default CompanyAccounting;